<template>
    <li class="bg-primary-5 rounded p-12">
        <div class="flex items-center gap-8">
            <checkbox
                v-model="done"
                @change="toggleFinished"
                :disabled="savingTask"
            />
            <div class="grow space-y-4">
                <div class="flex items-center gap-8">
                    <tooltip
                        :content="task.assignee ? $t('tasks.assigned_to', {subject: assignedToMe ? $t('general.me') : task.assignee.name}) : $t('tasks.not_assigned')"
                        class="cursor-default"
                    >
                        <avatar
                            :color="task.assignee ? undefined : styles.colors.primary['60']"
                            :image="task.assignee ? task.assignee.image_url : undefined"
                            size="small"
                        >
                            <svg-icon v-if="!task.assignee" type="person" />
                            <span v-else>{{ task.assignee.initials }}</span>
                        </avatar>
                    </tooltip>
                    <div class="grow">
                        <router-link
                            v-if="task.subject_id && !isOnApplicationPage"
                            :to="{ name: task.subject_type, params: { id: task.subject_id }}"
                        >
                            <simple-button
                                look="text"
                                display="neutral"
                                icon="person"
                                icon-position="left"
                            >
                                {{ typeof (task.subject_title) == 'object' ? task.subject_title[locale] : task.subject_title }}
                            </simple-button>
                        </router-link>
                        <div
                            v-if="dueDate"
                            :class="[ pastDue ? 'text-semantic-error' : done ? 'text-primary-40' : 'text-semantic-info']"
                            class="flex items-center gap-4"
                        >
                            <svg-icon class="w-18 h-18" type="calendar-today"></svg-icon>
                            <span :class="{ 'line-through': done }" class="text-body-sm font-semibold">{{ dueDate }}</span>
                        </div>
                    </div>
                    <div class="self-start flex items-center gap-12">
                        <template v-for="option in options">
                            <button :disabled="savingTask"
                                    class="text-primary"
                                    role="button"
                                    @click="option.action"
                            >
                                <svg-icon v-if="option.icon" :type="option.icon" class="w-16 h-16"/>
                            </button>
                        </template>
                    </div>
                </div>
                <div class="flex items-center">
                    <span
                        class="grow font-semibold"
                        :class="{'line-through text-primary': done}"
                    >
                        {{ task.title }}
                    </span>
                    <button v-if="task.notes" class="text-primary" @click="showNotes = !showNotes">
                        <svg-icon
                            class="w-18 h-18"
                            :class="{'rotate-180': !showNotes }"
                            type="keyboard-arrow-up"
                        />
                    </button>
                </div>
            </div>
        </div>
        <div
            v-if="task.notes"
            :class="[showNotes ? 'h-auto pt-4': 'h-0 overflow-hidden', { 'line-through text-primary-40': done }]"
            class="ml-28"
        >
            {{ task.notes }}
        </div>
    </li>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import Avatar from './Avatar.vue';
import Task from '../../models/Task';
import moment from 'moment';
import * as tasks from '../../services/TaskService';
import {ADD_NOTIFICATION} from '../../state/mutation-types';
import Tooltip from '../navigation/Tooltip.vue';
import Checkbox from "../forms/Checkbox.vue";
import RadioButton from "../forms/RadioButton.vue";
import SimpleButton from "./SimpleButton.vue";
import {mapActions, mapStores} from "pinia";
import {useMainStore} from "../../state/store";
import {useNotificationsStore} from "../../state/modules/notifications";
import {useRepo} from "pinia-orm";
import styles from '../../../../../../styles.config';

export default {
    components: {
        SimpleButton,
        RadioButton,
        Checkbox,
        'svg-icon': SvgIcon,
        'avatar': Avatar,
        'tooltip': Tooltip,
    },
    props: {
        task: {
            type: Task,
            required: true,
        },
        desc: Boolean,
        isOnApplicationPage: Boolean,
    },
    data: function () {
        return {
            styles,
            savingTask: false,
            showNotes: false,
        };
    },
    computed: {
        ...mapStores(useMainStore, useNotificationsStore),
        locale() {
            return this.mainStore.locale;
        },
        currentUserId() {
            return this.mainStore.currentUser.id;
        },
        dueDate() {
            return this.task.due_date ? moment().utc().locale(this.locale).to(this.task.due_date) : null;
        },
        done() {
            return this.task.finished_at && moment().utc().isAfter(this.task.finished_at);
        },
        pastDue() {
            return moment().utc().isAfter(this.task.due_date);
        },
        assignedToMe() {
            return this.task.assignee && this.task.assignee.id == this.currentUserId;
        },
        options() {
            return [
                ...[
                    this.assignedToMe
                        ? {}
                        : {label: this.$t('tasks.assign_to_me'), action: this.assignToMe},
                ],
                {label: this.$t('tasks.edit_task'), action: this.edit, icon: 'edit'},
                {label: this.$t('tasks.delete_task'), action: this.delete, icon: 'delete'},
            ].filter(value => JSON.stringify(value) !== '{}');
        },
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        toggleFinished() {
            this.savingTask = true;

            let oldState = this.task.finished_at ? moment(this.task.finished_at).utc() : null;
            let newState = oldState ? null : moment().utc();

            this.$emit('finished', {finished_at: newState, assignee: this.task.assignee_id});

            const successMessage = this.$t('tasks.finished_success', {state: this.$t(newState ? 'tasks.done' : 'tasks.todo')});
            const failureMessage = this.$t('tasks.task_save_error');

            tasks.update(this.task.id, {
                data: {attributes: {finished_at: newState ? newState.format('YYYY-MM-DD HH:mm:ss') : null}},
            }).then(result => {
                useRepo(Task).save({
                    id: this.task.id,
                    finished_at: newState ? newState.toISOString() : null,
                });
            }).then(result => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: successMessage
                });
            }).catch(error => {
                useRepo(Task).save({
                    id: this.task.id,
                    finished_at: oldState,
                });

                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: failureMessage,
                });

                this.$emit('finished', oldState);

            }).finally(() => {
                this.savingTask = false;
            });

            this.savingTask = false;
        },
        edit() {
            this.mainStore.startEditingTask(this.task);
        },
        delete() {
            this.mainStore.confirmDeleteTask(this.task);
        },
        assignToMe() {
            this.savingTask = true;

            let oldState = this.task.assignee_id;

            const failureMessage = this.$t('tasks.task_save_error');

            tasks.update(this.task.id, {
                data: {relationships: {assignee: {data: {id: this.currentUserId}}}},
            }).then(result => {
                useRepo(Task).save({
                    id: this.task.id,
                    assignee_id: this.currentUserId,
                });
            }).catch(error => {
                useRepo(Task).save({
                    id: this.task.id,
                    assignee_id: oldState,
                });

                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: failureMessage,
                });
            }).finally(() => {
                this.savingTask = false;
            });
        },
    },
};
</script>
